/* Multi-step callback form shown in a modal. Opened by any link pointing at #callback. */
(() => {
  const root = document.querySelector('[data-hm-callback]');
  if (!root) return;

  const dialog = root.querySelector('.hm-cb__dialog');
  const panels = Array.from(root.querySelectorAll('[data-cb-panel]'));
  const done = root.querySelector('[data-cb-done]');
  const nav = root.querySelector('[data-cb-nav]');
  const backBtn = root.querySelector('[data-cb-back]');
  const nextBtn = root.querySelector('[data-cb-next]');
  const sendBtn = root.querySelector('[data-cb-submit]');
  const errorEl = root.querySelector('[data-cb-error]');
  const reviewEl = root.querySelector('[data-cb-review]');
  const currentEl = root.querySelector('[data-cb-current]');
  const totalEl = root.querySelector('[data-cb-total]');
  const toast = root.querySelector('[data-cb-toast]');
  const toastStay = root.querySelector('[data-cb-toast-stay]');
  const topicQ = root.querySelector('[data-cb-topic-q]');
  const textBox = root.querySelector('[name="reach"][value="Text"]');
  const smsBlock = root.querySelector('[data-cb-sms]');
  const smsConsent = root.querySelector('[data-cb-sms-consent]');

  let step = 0;
  let submitted = false;
  let opener = null;
  let askedAboutRick = false;
  if (totalEl) totalEl.textContent = String(panels.length);

  const FOCUSABLE_FIELD = 'input, select, textarea';
  const fields = () => Array.from(root.querySelectorAll(FOCUSABLE_FIELD));
  const val = (name) => {
    const el = root.querySelector(`[name="${name}"]`);
    return el ? el.value.trim() : '';
  };
  const checked = (name) =>
    Array.from(root.querySelectorAll(`[name="${name}"]:checked`)).map((el) => el.value);

  /* The texting terms are only asked of people who actually want texts, so the block follows the "Text me"
     tick. Untick it and the SMS consent goes with it — a stale tick would otherwise be recorded as consent
     for a number that is no longer being given. */
  function syncSms() {
    if (!smsBlock) return;
    const wantsText = !!(textBox && textBox.checked);
    smsBlock.hidden = !wantsText;
    if (!wantsText && smsConsent) smsConsent.checked = false;
  }

  const smsAgreed = () => !!(textBox && textBox.checked && smsConsent && smsConsent.checked);

  /* What was on screen when they ticked, not what the code says today — the settings behind this wording are
     merchant-editable, so the record has to come from the rendered block. */
  const smsTermsShown = () => (smsBlock ? smsBlock.textContent.replace(/\s+/g, ' ').trim() : '');

  /* Klaviyo only accepts E.164 (+15551234567). A raw "555 123 4567" is rejected outright when the phone is
     all we have to go on, and — worse — silently dropped when an email is present, so the number Rick needs
     never arrives. Returns '' when it can't be normalised confidently, which is what the field check keys off.
     US/Canada assumed, matching the US-only time zone list this form offers. */
  function toE164(raw) {
    const s = (raw || '').replace(/[\s().-]/g, '');
    if (/^\+[1-9]\d{7,14}$/.test(s)) return s;
    const d = s.replace(/\D/g, '');
    if (d.length === 10) return `+1${d}`;
    if (d.length === 11 && d[0] === '1') return `+${d}`;
    return '';
  }

  /* --- appointment slots -------------------------------------------------- */
  /* Rick's availability is set once, in his own Pacific hours, and the markup renders it that way. Almost
     nobody filling this in is in Pacific, so every slot is relabelled here into the zone they pick. The
     offsets come from the browser's own time zone database rather than a table of our own, which is what
     keeps daylight saving right without an edit twice a year. */
  const PACIFIC = 'America/Los_Angeles';
  const tzSelect = root.querySelector('[name="timezone"]');
  const slotSelects = Array.from(root.querySelectorAll('[data-cb-slots]'));
  const slotNote = root.querySelector('[data-cb-slot-note]');

  /* Which real zone a merchant-written option line means. Matched on the words rather than the exact string,
     so renaming "Pacific (PT)" to "Pacific Time" in the theme editor doesn't quietly break the conversion. */
  const ZONE_PATTERNS = [
    [/hawaii|\bH[SD]?T\b/i, 'Pacific/Honolulu'],
    [/alaska|\bAK[SD]?T\b/i, 'America/Anchorage'],
    [/arizona/i, 'America/Phoenix'],
    [/pacific|\bP[SD]?T\b/i, PACIFIC],
    [/mountain|\bM[SD]?T\b/i, 'America/Denver'],
    [/central|\bC[SD]?T\b/i, 'America/Chicago'],
    [/eastern|\bE[SD]?T\b/i, 'America/New_York'],
  ];
  const zoneFor = (label) => (ZONE_PATTERNS.find(([re]) => re.test(label || '')) || [])[1] || '';

  /* The wall-clock reading of an instant in a given zone, which is all the arithmetic below needs. */
  function partsIn(tz, date) {
    const out = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
      .formatToParts(date)
      .forEach((p) => {
        if (p.type !== 'literal') out[p.type] = Number(p.value);
      });
    out.hour %= 24; /* some engines write midnight as 24 */
    return out;
  }

  const offsetMinutes = (tz, date) => {
    const p = partsIn(tz, date);
    return (Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(date.getTime() / 1000) * 1000) / 60000;
  };

  /* Today's instant of a Pacific wall-clock time. Anchoring on today rather than a fixed date means each
     zone's daylight-saving state is read as it stands now. */
  function pacificInstant(hour, minute) {
    const today = partsIn(PACIFIC, new Date());
    const guess = Date.UTC(today.year, today.month - 1, today.day, hour, minute);
    return new Date(guess - offsetMinutes(PACIFIC, new Date(guess)) * 60000);
  }

  function zoneAbbr(tz, date) {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  }

  const SLOT_RE = /^\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*(?:-|–|—|to)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*$/i;

  /* "11:00 AM - 1:00 PM" into start and end on a 24-hour clock. A missing am/pm on the first time is taken
     from the second, which is how "4 - 6 pm" gets written. Returns null for anything it can't read, and the
     option then keeps the wording the merchant typed rather than showing a converted guess. */
  function parseSlot(text) {
    const m = SLOT_RE.exec(text || '');
    if (!m) return null;
    const to24 = (h, meridiem) => {
      let hour = Number(h);
      if (/pm/i.test(meridiem) && hour < 12) hour += 12;
      if (/am/i.test(meridiem) && hour === 12) hour = 0;
      return hour;
    };
    const start = { hour: to24(m[1], m[3] || m[6] || ''), minute: Number(m[2] || 0) };
    const end = { hour: to24(m[4], m[6] || m[3] || ''), minute: Number(m[5] || 0) };
    if (start.hour > 23 || end.hour > 23) return null;
    return { start, end };
  }

  const clockLabel = (hour, minute) => {
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return minute ? `${h12}:${String(minute).padStart(2, '0')} ${meridiem}` : `${h12} ${meridiem}`;
  };

  /* Rewrites every slot option into the chosen zone. The options are edited in place and the selection put
     back by position, because the value changes with the zone and would otherwise be dropped. */
  function relabelSlots() {
    const tz = zoneFor(tzSelect ? tzSelect.value : '') || PACIFIC;
    let noteAbbr = '';
    slotSelects.forEach((select) => {
      const keep = select.selectedIndex;
      Array.from(select.querySelectorAll('[data-cb-slot]')).forEach((option) => {
        const slot = parseSlot(option.dataset.cbSlot);
        if (!slot) return;
        const from = pacificInstant(slot.start.hour, slot.start.minute);
        const to = pacificInstant(slot.end.hour, slot.end.minute);
        const ptAbbr = zoneAbbr(PACIFIC, from);
        const pacific = `${clockLabel(slot.start.hour, slot.start.minute)} – ${clockLabel(slot.end.hour, slot.end.minute)} ${ptAbbr}`;
        if (tz === PACIFIC) {
          option.textContent = pacific;
          option.value = pacific;
          noteAbbr = ptAbbr;
          return;
        }
        const s = partsIn(tz, from);
        const e = partsIn(tz, to);
        noteAbbr = zoneAbbr(tz, from);
        const local = `${clockLabel(s.hour, s.minute)} – ${clockLabel(e.hour, e.minute)} ${noteAbbr}`;
        option.textContent = local;
        /* Rick works in Pacific, so the recorded answer carries both readings and he never has to convert. */
        option.value = `${local} (${pacific})`;
      });
      select.selectedIndex = keep;
    });
    if (slotNote) {
      slotNote.textContent = noteAbbr ? `Times are shown in ${noteAbbr}.` : '';
      slotNote.hidden = !noteAbbr;
    }
  }

  /* Pre-select the zone the browser reports, so the slots read correctly for most people without them
     touching the dropdown. Matched on the zone itself first and on today's offset second, which covers the
     many US zone names that are aliases of the five offered here. Anyone we guess wrong can still change it,
     and every option says out loud which zone it is in. */
  function guessZone() {
    if (!tzSelect || tzSelect.value) return;
    try {
      const here = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!here) return;
      const now = new Date();
      const options = Array.from(tzSelect.options).filter((o) => o.value);
      const match =
        options.find((o) => zoneFor(o.value) === here) ||
        options.find((o) => {
          const tz = zoneFor(o.value);
          return tz && offsetMinutes(tz, now) === offsetMinutes(here, now);
        });
      if (match) tzSelect.value = match.value;
    } catch {
      /* An unknown zone name only costs the head start; the dropdown still works by hand. */
    }
  }

  /* Anything typed or ticked counts as work in progress, which is what the leave warning keys off. */
  const isDirty = () =>
    !submitted &&
    fields().some((el) => (el.type === 'checkbox' || el.type === 'radio' ? el.checked : el.value.trim() !== ''));

  /* Each step decides for itself whether Next should light up. */
  function stepValid(i) {
    const panel = panels[i];
    if (!panel) return false;

    const text = panel.querySelector('input[type="text"][data-cb-required]');
    if (text) return text.value.trim() !== '';

    const group = panel.querySelector('[data-cb-group][data-cb-required]');
    if (group) {
      const kind = group.dataset.cbGroup;
      const ticked = Array.from(group.querySelectorAll('input:checked'));
      if (!ticked.length) return false;

      /* "Other" only counts once something has been written in it, and a contact method only counts
         once the number or address it needs has been filled in. */
      const ok = ticked.every((box) => {
        const key = box.dataset.cbReveals;
        if (!key) return true;
        const partner = group.querySelector(`[data-cb-revealed="${key}"]`);
        if (!partner) return true;
        if (partner.value.trim() === '') return false;
        if (partner.type === 'email') return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(partner.value.trim());
        /* Has to be a number Klaviyo will actually take, checked here rather than at the end — seven digits
           passed this gate and then failed the submit, stranding people on the last step. */
        if (partner.type === 'tel') return toE164(partner.value) !== '';
        return true;
      });
      if (!ok) return false;

      if (kind === 'contact') {
        const consent = panel.querySelector('[data-cb-consent]');
        if (consent && !consent.checked) return false;
        /* Texts need their own tick on top of the general permission — that is the bit Twilio and Klaviyo ask for. */
        if (textBox && textBox.checked && smsConsent && !smsConsent.checked) return false;
      }
      return true;
    }
    return true;
  }

  function render() {
    panels.forEach((p, i) => (p.hidden = i !== step));
    if (currentEl) currentEl.textContent = String(step + 1);
    backBtn.hidden = step === 0;
    const last = step === panels.length - 1;
    nextBtn.hidden = last;
    sendBtn.hidden = !last;
    const ready = stepValid(step);
    nextBtn.disabled = !ready;
    nextBtn.setAttribute('aria-disabled', String(!ready));
    if (last) buildReview();
  }

  function buildReview() {
    if (!reviewEl) return;
    const other = val('interests_other');
    const interests = checked('interests').map((v) => (v === 'Other' && other ? `Other: ${other}` : v));
    const reach = checked('reach').map((v) => {
      if (v === 'Text') return `Text ${val('text_number')}`;
      if (v === 'Phone') return `Call ${val('phone_number')}`;
      return `Email ${val('email')}`;
    });
    const when = [1, 2, 3]
      .map((n) => [val(`time_${n}`), val(`day_${n}`)].filter(Boolean).join(', '))
      .filter(Boolean);

    const rows = [
      ['Name', val('first_name')],
      ['Interested in', interests.join(', ')],
      ['Tests per month', checked('volume')[0] || ''],
      [askedAboutRick ? 'To talk about' : 'Your questions', val('topic')],
      ['Reach me by', reach.join(', ')],
      ['Text messages', smsAgreed() ? 'You agreed to the texting terms' : ''],
      ['Best times', when.join(' · ')],
      ['Time zone', val('timezone')],
    ];
    reviewEl.innerHTML = rows
      .filter(([, v]) => v)
      .map(([k, v]) => `<dt>${k}</dt><dd>${escapeHtml(v)}</dd>`)
      .join('');
  }

  const escapeHtml = (s) =>
    s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function payload() {
    const other = val('interests_other');
    const interests = checked('interests').map((v) => (v === 'Other' && other ? `Other: ${other}` : v));
    /* When someone gives a landline to ring and a mobile to text, the texting number is the one that has to
       reach Klaviyo — it is the number they consented about, and the only one that can receive an SMS. */
    const smsNumber = toE164(val('text_number'));
    const voiceNumber = toE164(val('phone_number'));
    /* Our own record of the moment they ticked, kept as a profile property Rick can read. Klaviyo stamps the
       subscription's own consent time server-side — see the subscriptions block in submit() for why ours must
       not be sent with it. */
    const consentAt = smsAgreed() ? new Date().toISOString() : '';
    return {
      first_name: val('first_name'),
      email: val('email'),
      phone: smsAgreed() ? smsNumber || voiceNumber : voiceNumber || smsNumber,
      properties: {
        Interests: interests.join(', '),
        TestsPerMonth: checked('volume')[0] || '',
        /* Both buttons feed the same list, so record which one was pressed — otherwise there is nothing in
           Klaviyo to say whether "Questions" is a question for us or a topic for Rick. */
        Questions: val('topic'),
        OpenedFrom: opener ? (opener.textContent || '').trim() : '',
        ContactMethods: checked('reach').join(', '),
        TextNumber: val('text_number'),
        PhoneNumber: val('phone_number'),
        BestTimes: [1, 2, 3].map((n) => [val(`time_${n}`), val(`day_${n}`)].filter(Boolean).join(' ')).filter(Boolean).join(' | '),
        TimeZone: val('timezone'),
        ContactConsent: root.querySelector('[data-cb-consent]').checked ? 'Yes' : 'No',
        /* Proof of consent, which is what both Twilio and Klaviyo want kept: whether they agreed, the exact
           wording they agreed to, and when and where they agreed to it. */
        SMSConsent: smsAgreed() ? 'Yes' : 'No',
        SMSConsentText: smsAgreed() ? smsTermsShown() : '',
        SMSConsentAt: consentAt,
        SMSConsentPage: smsAgreed() ? window.location.href : '',
        Source: 'Homepage callback form',
      },
    };
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  async function submit() {
    const listId = (root.dataset.klaviyoList || '').trim();
    const companyId = klaviyoCompanyId();

    /* Refuse rather than swallow someone's details: without a destination there is nowhere for this to go. */
    if (!listId || !companyId) {
      showError('This form isn\'t connected to a mailing list yet, so it can\'t be sent. Please ring or email us instead.');
      return;
    }

    const data = payload();
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending…';
    showError('');

    try {
      const attributes = { properties: data.properties };
      if (data.email) attributes.email = data.email;
      if (data.phone) attributes.phone_number = data.phone;
      if (data.first_name) attributes.first_name = data.first_name;

      /* The tick on the form is only half of it. Without this block Klaviyo stores the number but leaves the
         profile unsubscribed from SMS, so nothing can ever be sent to it — the consent is collected and then
         goes nowhere. Only ever sent when they actually ticked the texting terms.
         Needs revision 2025-07-15 or later: on 2024-10-15 this endpoint rejects 'subscriptions' outright.
         No consented_at: Klaviyo reserves that field for back-filling consent gathered elsewhere and rejects
         the whole request with "Invalid consent timestamp" unless historical_import rides along — and that
         flag is wrong for someone consenting right now, because it suppresses the flows the subscription is
         meant to trigger. Klaviyo timestamps the consent itself at the moment of this call; our own
         SMSConsentAt property keeps the matching record on our side. */
      if (data.phone && smsAgreed()) {
        attributes.subscriptions = {
          sms: { marketing: { consent: 'SUBSCRIBED' } },
        };
      }

      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2025-07-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes } } },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      });
      if (!res.ok) {
        const detail = await klaviyoDetail(res);
        throw Object.assign(new Error(`Klaviyo responded ${res.status}${detail ? `: ${detail}` : ''}`), { detail });
      }

      submitted = true;
      panels.forEach((p) => (p.hidden = true));
      nav.hidden = true;
      const count = root.querySelector('.hm-cb__count');
      if (count) count.hidden = true;
      done.hidden = false;
    } catch (err) {
      sendBtn.disabled = false;
      sendBtn.textContent = 'Send to Rick';
      /* A rejected number is the one failure the sender can actually do something about, so say so instead of
         offering "try again" on something that will fail identically every time. */
      showError(
        /phone/i.test(err.detail || '')
          ? "That mobile number doesn't look like one that can receive texts. Please check it, or pick another way for us to reach you."
          : 'Something went wrong sending that. Please try again, or ring us.'
      );
      console.error('[callback form]', err);
    }
  }

  /* Klaviyo puts the useful part in the body, not the status. Without this a 400 reads as "responded 400",
     which is what made the last one guesswork. */
  async function klaviyoDetail(res) {
    try {
      const body = await res.json();
      return (body.errors || []).map((e) => e.detail).filter(Boolean).join('; ');
    } catch {
      return '';
    }
  }

  function klaviyoCompanyId() {
    const tag = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
    if (!tag) return '';
    const m = tag.src.match(/company_id=([A-Za-z0-9]+)/);
    return m ? m[1] : '';
  }

  /* --- open / close ------------------------------------------------------ */
  function open(trigger) {
    opener = trigger || null;
    /* Two buttons open the same form, so the heading echoes whichever one was pressed. Falls back to the
       setting if something opens it without a label. */
    const title = root.querySelector('.hm-cb__title');
    const label = trigger ? (trigger.textContent || '').trim() : '';
    if (title && label) title.textContent = label;

    /* "I'd like to talk to Rick." and "I have more questions." run the same form, so the open question is
       worded for whichever one was pressed. Keyed off the label rather than the link, because both buttons
       point at #callback and the wording is the only thing that tells them apart. */
    askedAboutRick = /rick/i.test(label);
    if (topicQ) {
      const rickQ = topicQ.dataset.qRick;
      topicQ.textContent = askedAboutRick && rickQ ? rickQ : topicQ.dataset.qDefault;
    }

    root.hidden = false;
    document.body.style.overflow = 'hidden';
    hideToast();
    step = 0;
    syncSms();
    render();
    const first = panels[0].querySelector(FOCUSABLE_FIELD);
    if (first) first.focus();
  }

  /* Half-filled forms ask before closing. The ask is our own toast rather than window.confirm(), and the
     "keep filling this in" button takes the focus, so Enter — the default — cancels and returns to the form. */
  function close() {
    if (!isDirty()) return dismiss();
    if (!toast) return dismiss(); /* no toast markup means no way to ask, so don't trap anyone in the form */
    toast.hidden = false;
    toastStay.focus();
  }

  function dismiss() {
    hideToast();
    root.hidden = true;
    document.body.style.overflow = '';
    if (opener && typeof opener.focus === 'function') opener.focus();
  }

  function hideToast() {
    if (toast) toast.hidden = true;
  }

  /* Cancel: drop the toast and hand focus back to the question they were on. The last step has no fields,
     so there we fall back to whichever button that step is showing. */
  function stay() {
    hideToast();
    const back = (panels[step] && panels[step].querySelector(FOCUSABLE_FIELD)) || (sendBtn.hidden ? nextBtn : sendBtn);
    if (back && typeof back.focus === 'function') back.focus();
  }

  const toastOpen = () => !!toast && !toast.hidden;

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('a[href$="#callback"], a[href="#callback"]');
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }
    if (e.target.closest('[data-cb-toast-stay]')) return stay();
    if (e.target.closest('[data-cb-toast-leave]')) return dismiss();
    if (toastOpen()) return; /* while the toast is up it owns the decision, including scrim clicks */
    if (e.target.closest('[data-cb-close]')) close();
  });

  /* On the document rather than the form, so the toast still answers to the keyboard if a stray click has
     dropped the focus onto the page body. Escape and Enter both mean cancel; only the leave button leaves. */
  document.addEventListener('keydown', (e) => {
    if (!toastOpen()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      stay();
      return;
    }
    if (e.key === 'Tab') {
      trapFocus(e, toast);
      return;
    }
    if (e.key === 'Enter' && !toast.contains(e.target)) {
      e.preventDefault();
      stay();
    }
  });

  root.addEventListener('keydown', (e) => {
    if (toastOpen()) return; /* the toast handles its own keys above */
    if (e.key === 'Escape') close();
    if (e.key === 'Tab') trapFocus(e, dialog);
    if (e.key === 'Enter' && e.target.matches('input[type="text"], input[type="email"], input[type="tel"]')) {
      e.preventDefault();
      if (stepValid(step)) nextBtn.click();
    }
  });

  function trapFocus(e, container) {
    const focusable = Array.from(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([hidden]), select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    /* A click on the scrim leaves the focus on the page body; pull it back rather than tabbing out. */
    if (!container.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* --- interaction ------------------------------------------------------- */
  root.addEventListener('input', () => render());
  root.addEventListener('change', (e) => {
    const box = e.target.closest('[data-cb-reveals]');
    if (box) {
      const partner = root.querySelector(`[data-cb-revealed="${box.dataset.cbReveals}"]`);
      if (partner) {
        partner.hidden = !box.checked;
        if (box.checked) partner.focus();
        else partner.value = '';
      }
    }
    if (tzSelect && e.target === tzSelect) relabelSlots();
    syncSms();
    render();
  });

  nextBtn.addEventListener('click', () => {
    if (!stepValid(step)) return;
    if (step < panels.length - 1) step += 1;
    showError('');
    render();
    const focusMe = panels[step].querySelector(FOCUSABLE_FIELD);
    if (focusMe) focusMe.focus();
  });

  backBtn.addEventListener('click', () => {
    if (step > 0) step -= 1;
    showError('');
    render();
  });

  sendBtn.addEventListener('click', submit);

  window.addEventListener('beforeunload', (e) => {
    if (root.hidden || !isDirty()) return;
    e.preventDefault();
    e.returnValue = '';
  });

  guessZone();
  relabelSlots();
  syncSms();
  render();
})();
